import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { muiTheme } from '@pbt/pbt-ui-components'

import Link from '~/components/common/link/Link'
import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { QuestionTimelineItem } from '~/types'
import { isDifferedBusinessList } from '~/utils/businessUtils'
import { usePersistClickedItemOnURL } from '~/utils/routes'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import TimelineCard, { type TimelineCardProps } from '../TimelineCard'
import TimelineCardActions from '../TimelineCardActions'
import TimelineCardContent from '../TimelineCardContent'
import QuestionCardContent from './QuestionCardContent'
import QuestionCardContentNew from './questionCardV2/QuestionCardContentNew'
import QuestionTitle from './QuestionTitle'

interface QuestionCardProps extends TimelineCardProps {
  index: number
  item: QuestionTimelineItem
}

const QuestionCard = ({ item, index, ...rest }: QuestionCardProps) => {
  const { t } = useTranslation('Common')
  const isCVCIntakeQuestionnaireEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_INTAKE_QUESTIONNAIRE),
  )
  const isQuestionsDynamicTextAndSubtextEnabled = useSelector(
    getFeatureToggle(FeatureToggle.QUESTIONS_DYNAMIC_TEXT_AND_SUBTEXT),
  )
  const hasMultipleQuestions = item.entries.length > 1
  const { businessId, appointmentId } = item.entries[0]

  const isContextFirstItem = useIsCurrentContextItem({ businessId })
  const persistClickedItem = usePersistClickedItemOnURL()

  const hasDifferedBusinessEntries = isDifferedBusinessList(item.entries)

  return (
    <TimelineCard
      originBusinessId={businessId}
      title={
        <QuestionTitle
          questionSubText={
            isQuestionsDynamicTextAndSubtextEnabled
              ? item.questionSubText
              : undefined
          }
          questionSubTextStyle={{
            fontSize: muiTheme.typography.body1.fontSize,
          }}
          questionText={item.questionText}
          questionTextStyle={{
            fontSize: muiTheme.typography.h3.fontSize,
          }}
        />
      }
      {...item}
      {...rest}
    >
      <TimelineCardContent noPadding noTypography>
        {isCVCIntakeQuestionnaireEnabled ? (
          <QuestionCardContentNew
            entries={item.entries}
            hasDifferedBusinessEntries={hasDifferedBusinessEntries}
          />
        ) : (
          <QuestionCardContent
            entries={item.entries}
            hasDifferedBusinessEntries={hasDifferedBusinessEntries}
          />
        )}
      </TimelineCardContent>
      {!hasMultipleQuestions && isContextFirstItem && appointmentId && (
        <TimelineCardActions>
          <Link
            to={`/scheduler/schedule/${appointmentId}`}
            onClick={() => persistClickedItem(index)}
          >
            {t('VIEW_APPOINTMENT')}
          </Link>
        </TimelineCardActions>
      )}
    </TimelineCard>
  )
}

export default QuestionCard
